import { useLocation } from "react-router-dom";

const bookNameFromChapter = (chapter) => {
  const raw = chapter?.bookName || "";
  return raw.includes("-") ? raw.split("-").slice(1).join("-") : raw;
};

const zhCopy = {
  formats: [
    { id: "reference", label: "出处" },
    { id: "plain", label: "纯文" },
    { id: "quote", label: "金句" },
  ],
  collected: (count) => `已收集 ${count} 节`,
  expandPrev: "包含上一节",
  expandNext: "包含下一节",
  clear: "取消选中",
  copy: "复制经文",
  copied: "已抄入剪贴板",
  copyShort: "复制",
  copiedShort: "已复制",
  share: "分享",
  listen: "从本节朗读",
  listenSelection: "朗读本段",
  listenShort: "朗读",
  note: "写笔记",
  noteShort: "笔记",
  hint: "点另一节扩展选区 · ⌘/Ctrl+C 复制 · Esc 取消",
  region: "已选经文",
  highlight: "高亮划线",
  clearHighlight: "清除高亮",
};

const enCopy = {
  formats: [
    { id: "reference", label: "Ref" },
    { id: "plain", label: "Plain" },
    { id: "quote", label: "Quote" },
  ],
  collected: (count) =>
    `${count} ${count === 1 ? "verse" : "verses"} gathered`,
  expandPrev: "Include previous verse",
  expandNext: "Include next verse",
  clear: "Clear selection",
  copy: "Copy passage",
  copied: "Copied",
  copyShort: "Copy",
  copiedShort: "Copied",
  share: "Share",
  listen: "Listen",
  listenSelection: "Listen passage",
  listenShort: "Play",
  note: "Take Note",
  noteShort: "Note",
  hint: "Tap another verse to expand · ⌘/Ctrl+C to copy · Esc to clear",
  region: "Selected verses",
  highlight: "Highlight",
  clearHighlight: "Clear highlight",
};

export const EDITIONS = {
  zh: {
    id: "zh",
    versionName: "和合本",
    shortLabel: "中文",
    appTitle: "圣经",
    homePath: "/",
    chapterPath: (id) => `/chapter/${id}`,
    usesPinyin: true,
    quoteDash: "——",
    hotBooks: [
      "创世记",
      "出埃及记",
      "诗篇",
      "箴言",
      "马太福音",
      "约翰福音",
      "罗马书",
      "启示录",
    ],
    loadChapters: () =>
      import("../data/bibleData").then((module) => module.bibleChapters),
    loadTitles: () => import("../data/chapterTitles"),
    copy: zhCopy,
    ui: {
      homeTitle: "圣经章节",
      searchPlaceholder: "搜索书卷、章节或经文，如 约翰、约3:16、神爱世人",
      clearSearch: "清空搜索",
      searchTips: "可以搜:",
      tipPinyin: "拼音 matai",
      tipContent: "经文 神爱世人",
      tipFuzzy: "出处 约3:16",
      tipClear: "清空",
      hotBooks: "🔥 热门书卷",
      allBooks: "📖 全部书卷",
      oldTestament: "旧约 (39卷)",
      newTestament: "新约 (27卷)",
      bookNav: "书卷导航",
      searchResults: (term) => `“${term}”`,
      foundCount: (n) => (n > 0 ? `${n} 个结果` : "没有匹配"),
      searchHidesNav: "",
      clickToRead: "点击阅读本章内容...",
      noMatchTitle: "没有找到相关内容",
      noMatchHint: "试试书卷名、拼音，或一节经文里的原句",
      suggestions: "可以这样搜:",
      suggestPinyin: "matai",
      suggestBook: "约翰",
      suggestChapter: "约3:16",
      suggestText: "神爱世人",
      groupBooks: "书卷",
      groupHeadings: "章节",
      groupVerses: "经文",
      filterAll: "全部",
      bookMatchName: "书卷名",
      bookMatchPinyin: "拼音",
      bookMatchContains: "名称包含",
      headingMatch: "小标题",
      refMatch: "出处",
      moreInChapter: (n) => `本章另有 ${n} 处`,
      chapterCountLabel: (n) => `${n} 章`,
      openBook: "查看各章",
      openChapter: "阅读本章",
      pageStatus: (page, total, count) =>
        `第 ${page} 页，共 ${total} 页 （共 ${count} 章）`,
      chooseChapter: (book) => `${book} - 选择章节`,
      close: "close",
      notFound: "未找到章节",
      back: "返回",
      selectHintMobile: "点选一节，再点另一节，收集成段经文",
      selectHintDesktop: "点选经文收集段落，或划选后自动收成选区",
      prevChapter: "上一章",
      nextChapter: "下一章",
      jumpTo: "跳转到",
      catalog: "目录",
      catalogTitle: "圣经目录",
      catalogSearchPlaceholder: "搜索书卷、简写或拼音（如：创、cyj、John）",
      catalogAllBooks: "全部 (66)",
      catalogCurrent: "当前",
      catalogRead: "已读",
      catalogNoMatch: "未找到匹配的书卷",
      railCatalog: "目录",
      railSettings: "设置",
      railBookmarks: "书签",
      railNotes: "笔记",
      railHome: "首页",
      railPrev: "上一章",
      railNext: "下一章",
      railVerseComments: "节评",
      railTop: "顶部",
      railFullscreen: "全屏",
      railExitFullscreen: "退出",
      railFocus: "专注",
      railExitFocus: "退出",
      chapterMetaLine: (verses, count, minutes) =>
        `共 ${verses} 节 · 约 ${count.toLocaleString()} 字 · 预计 ${minutes} 分钟`,
      chapterEndDivider: "— 本章完 —",
      chapterStatsSummary: (highlightsCount, notesCount) =>
        `本章标记：高亮 ${highlightsCount} 处 · 笔记 ${notesCount} 条`,
      markChapterRead: "标记本章已读",
      chapterMarkedRead: "本章已读完",
      chapterSharedNotesTitle: "本章分享（已审核）",
      pullNextHint: (title, subtitle) =>
        `继续上拉进入下一章：${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextRelease: (title, subtitle) =>
        `松手进入下一章：${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextBibleEnd: "全本圣经已读完 🎉",
      copyText: "复制文字",
      copyWithRef: "复制含章节",
      shareWhatsApp: "WhatsApp 分享",
      listen: "朗读",
      listenSelection: "朗读选中",
      listenChapter: "朗读本章",
      listenFromHere: "从此处朗读",
      listenSections: "从小结开始",
      pause: "暂停",
      resume: "继续",
      stop: "停止",
      skipPrev: "上一节",
      skipNext: "下一节",
      rate: "语速",
      voice: "音色",
      voiceAuto: "自动",
      autoAdvance: "连读下一章",
      autoAdvanceOn: "读完自动下一章",
      autoAdvanceOff: "读完即停",
      sleepTimer: "定时停止",
      sleepTimerOff: "关闭定时",
      sleepTimerChapter: "读完本章即停",
      sleepTimerMin: (m) => `${m} 分钟`,
      zenMode: "专注阅读 (Z)",
      exitZenMode: "退出专注 (Esc)",
      fullscreen: "全屏阅读 (F)",
      exitFullscreen: "退出全屏 (Esc)",
      wakeLock: "屏幕常亮",
      wakeLockActive: "屏幕常亮中 (点击关闭)",
      wakeLockInactive: "屏幕常亮已关闭 (点击开启)",
      wakeLockDesc: "读经时保持屏幕常亮，防止设备休眠锁屏",
      wakeLockUnsupported: "当前设备或浏览器不支持屏幕常亮",
      currentChapterLabel: (book, chapterNum, total) =>
        `当前是：${book} 第 ${chapterNum} 章${total ? `（共 ${total} 章）` : ""}`,
      moreControls: "朗读设置",
      speechHint: "用系统语音朗读经文",
      speechUnsupported: "当前浏览器不支持朗读，请使用 Chrome 或 Safari",
      collapseSpeech: "收起到侧边悬浮",
      expandSpeech: "展开朗读",
      listeningVerse: (n, lang) =>
        String(lang || "").startsWith("en")
          ? `第 ${n} 节 · EN`
          : `第 ${n} 节`,
      readingProgress: "章节阅读进度",
      currentReading: "当前阅读",
      collapseProgress: "收起为极简微标",
      expandProgress: "展开进度胶囊",
      progressStyle: "进度条显示",
      scrollToTop: "回到顶部",
    },
  },
  en: {
    id: "en",
    versionName: "King James Version",
    shortLabel: "EN",
    appTitle: "Bible",
    homePath: "/en",
    chapterPath: (id) => `/en/chapter/${id}`,
    usesPinyin: false,
    quoteDash: "—",
    hotBooks: [
      "Genesis",
      "Exodus",
      "Psalms",
      "Proverbs",
      "Matthew",
      "John",
      "Romans",
      "Revelation",
    ],
    loadChapters: () =>
      import("../data/bibleDataEn").then((module) => module.bibleChapters),
    loadTitles: () =>
      Promise.resolve({ chapterTitles: {}, chapterSectionHeadings: {} }),
    copy: enCopy,
    ui: {
      homeTitle: "Holy Bible",
      searchPlaceholder: "Search a book, chapter, or verse — John, John 3:16",
      clearSearch: "Clear search",
      searchTips: "Try:",
      tipPinyin: "John",
      tipContent: "For God so loved",
      tipFuzzy: "John 3:16",
      tipClear: "Clear",
      hotBooks: "🔥 Popular books",
      allBooks: "📖 All books",
      oldTestament: "Old Testament (39)",
      newTestament: "New Testament (27)",
      bookNav: "Books",
      searchResults: (term) => `“${term}”`,
      foundCount: (n) => (n > 0 ? `${n} results` : "No matches"),
      searchHidesNav: "",
      clickToRead: "Open this chapter...",
      noMatchTitle: "Nothing matched",
      noMatchHint: "Try a book name or a few words from a verse",
      suggestions: "Try:",
      suggestPinyin: "John",
      suggestBook: "Matthew",
      suggestChapter: "John 3:16",
      suggestText: "In the beginning",
      groupBooks: "Books",
      groupHeadings: "Chapters",
      groupVerses: "Verses",
      filterAll: "All",
      bookMatchName: "Book",
      bookMatchPinyin: "Pinyin",
      bookMatchContains: "Name",
      headingMatch: "Heading",
      refMatch: "Reference",
      moreInChapter: (n) => `${n} more in this chapter`,
      chapterCountLabel: (n) => `${n} chapters`,
      openBook: "See chapters",
      openChapter: "Read chapter",
      pageStatus: (page, total, count) =>
        `Page ${page} of ${total} (${count} chapters)`,
      chooseChapter: (book) => `${book} — choose a chapter`,
      close: "close",
      notFound: "Chapter not found",
      back: "Back",
      selectHintMobile: "Tap one verse, then another, to gather a passage",
      selectHintDesktop:
        "Tap verses to gather a passage, or drag to select a range",
      prevChapter: "Previous",
      nextChapter: "Next",
      jumpTo: "Jump to",
      catalog: "Catalog",
      catalogTitle: "Bible Catalog",
      catalogSearchPlaceholder: "Search book name or abbreviation (e.g. Gen, John)",
      catalogAllBooks: "All (66)",
      catalogCurrent: "Current",
      catalogRead: "Read",
      catalogNoMatch: "No matching books found",
      railCatalog: "Catalog",
      railSettings: "Settings",
      railBookmarks: "Bookmarks",
      railNotes: "Notes",
      railHome: "Home",
      railPrev: "Prev",
      railNext: "Next",
      railVerseComments: "Notes",
      railTop: "Top",
      railFullscreen: "Fullscreen",
      railExitFullscreen: "Exit",
      railFocus: "Focus",
      railExitFocus: "Exit",
      chapterMetaLine: (verses, count, minutes) =>
        `${verses} verses · ~${count.toLocaleString()} words · ~${minutes} min read`,
      chapterEndDivider: "— End of Chapter —",
      chapterStatsSummary: (highlightsCount, notesCount) =>
        `Your marks: ${highlightsCount} highlights · ${notesCount} notes`,
      markChapterRead: "Mark Chapter as Read",
      chapterMarkedRead: "Chapter Completed",
      chapterSharedNotesTitle: "Community Reflections",
      pullNextHint: (title, subtitle) =>
        `Pull up to read next: ${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextRelease: (title, subtitle) =>
        `Release for next chapter: ${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextBibleEnd: "You have reached the end of the Bible 🎉",
      copyText: "Copy text",
      copyWithRef: "Copy with reference",
      shareWhatsApp: "Share on WhatsApp",
      listen: "Listen",
      listenSelection: "Read selection",
      listenChapter: "Read chapter",
      listenFromHere: "Listen from here",
      listenSections: "Start from a heading",
      pause: "Pause",
      resume: "Resume",
      stop: "Stop",
      skipPrev: "Previous verse",
      skipNext: "Next verse",
      rate: "Speed",
      voice: "Voice",
      voiceAuto: "Auto",
      autoAdvance: "Continue to next chapter",
      autoAdvanceOn: "Continue to the next chapter",
      autoAdvanceOff: "Stop at the end",
      sleepTimer: "Sleep timer",
      sleepTimerOff: "Off",
      sleepTimerChapter: "End of chapter",
      sleepTimerMin: (m) => `${m} min`,
      zenMode: "Focus mode (Z)",
      exitZenMode: "Exit focus (Esc)",
      fullscreen: "Full screen (F)",
      exitFullscreen: "Exit full screen (Esc)",
      wakeLock: "Screen Awake",
      wakeLockActive: "Screen awake (click to turn off)",
      wakeLockInactive: "Screen awake off (click to turn on)",
      wakeLockDesc: "Keep screen awake while reading to prevent sleep or auto-lock",
      wakeLockUnsupported: "Screen wake lock is not supported on this browser",
      currentChapterLabel: (book, chapterNum, total) =>
        `Current: ${book} Chapter ${chapterNum}${total ? ` (of ${total})` : ""}`,
      moreControls: "Listening settings",
      speechHint: "Read aloud with your device voice",
      speechUnsupported: "This browser cannot read aloud. Try Chrome or Safari.",
      collapseSpeech: "Collapse player",
      expandSpeech: "Expand player",
      listeningVerse: (n) => `Verse ${n}`,
      readingProgress: "Reading Progress",
      currentReading: "Current Reading",
      collapseProgress: "Collapse to minimal badge",
      expandProgress: "Expand progress capsule",
      progressStyle: "Progress Display",
      scrollToTop: "Scroll to top",
    },
  },
  compare: {
    id: "compare",
    versionName: "和合本 · King James Version",
    shortLabel: "对照",
    appTitle: "对照",
    homePath: "/compare",
    chapterPath: (id) => `/compare/chapter/${id}`,
    usesPinyin: true,
    quoteDash: "——",
    hotBooks: [
      "创世记",
      "出埃及记",
      "诗篇",
      "箴言",
      "马太福音",
      "约翰福音",
      "罗马书",
      "启示录",
    ],
    loadChapters: () => loadCompareChapters(),
    loadTitles: () => import("../data/chapterTitles"),
    copy: {
      formats: [
        { id: "parallel", label: "对照" },
        { id: "zh", label: "中文" },
        { id: "en", label: "EN" },
      ],
      collected: (count) => `已收集 ${count} 节`,
      expandPrev: "包含上一节",
      expandNext: "包含下一节",
      clear: "取消选中",
      copy: "复制对照",
      copied: "已抄入剪贴板",
      copyShort: "复制",
      copiedShort: "已复制",
      share: "分享",
      listen: "从本节朗读",
      listenSelection: "朗读本段",
      listenShort: "朗读",
      note: "写笔记",
      noteShort: "笔记",
      hint: "点另一节扩展选区 · ⌘/Ctrl+C 复制对照 · Esc 取消",
      region: "已选经文",
      highlight: "高亮划线",
      clearHighlight: "清除高亮",
    },
    ui: {
      homeTitle: "中英对照",
      searchPlaceholder: "搜索中文或英文，如 约翰、John 3:16、神爱世人",
      clearSearch: "清空搜索",
      searchTips: "可以搜:",
      tipPinyin: "拼音 matai",
      tipContent: "经文 神爱世人",
      tipFuzzy: "出处 约3:16",
      tipClear: "清空",
      hotBooks: "🔥 热门书卷",
      allBooks: "📖 全部书卷",
      oldTestament: "旧约 (39卷)",
      newTestament: "新约 (27卷)",
      bookNav: "书卷导航",
      searchResults: (term) => `“${term}”`,
      foundCount: (n) => (n > 0 ? `${n} 个结果` : "没有匹配"),
      searchHidesNav: "",
      clickToRead: "打开中英对照...",
      noMatchTitle: "没有找到相关内容",
      noMatchHint: "试试中文书卷、英文书名，或一句经文",
      suggestions: "可以这样搜:",
      suggestPinyin: "John",
      suggestBook: "约翰",
      suggestChapter: "约3:16",
      suggestText: "神爱世人",
      groupBooks: "书卷",
      groupHeadings: "章节",
      groupVerses: "经文",
      filterAll: "全部",
      bookMatchName: "书卷名",
      bookMatchPinyin: "拼音",
      bookMatchContains: "名称包含",
      headingMatch: "小标题",
      refMatch: "出处",
      moreInChapter: (n) => `本章另有 ${n} 处`,
      chapterCountLabel: (n) => `${n} 章`,
      openBook: "查看各章",
      openChapter: "阅读本章",
      pageStatus: (page, total, count) =>
        `第 ${page} 页，共 ${total} 页 （共 ${count} 章）`,
      chooseChapter: (book) => `${book} - 选择章节`,
      close: "close",
      notFound: "未找到章节",
      back: "返回",
      selectHintMobile: "点选一节，同时对照中英文",
      selectHintDesktop: "点选经文收集段落，复制中英对照",
      prevChapter: "上一章",
      nextChapter: "下一章",
      jumpTo: "跳转到",
      catalog: "目录",
      catalogTitle: "圣经目录",
      catalogSearchPlaceholder: "搜索书卷、简写或拼音（如：创、cyj、John）",
      catalogAllBooks: "全部 (66)",
      catalogCurrent: "当前",
      catalogRead: "已读",
      catalogNoMatch: "未找到匹配的书卷",
      railCatalog: "目录",
      railSettings: "设置",
      railBookmarks: "书签",
      railNotes: "笔记",
      railHome: "首页",
      railPrev: "上一章",
      railNext: "下一章",
      railVerseComments: "节评",
      railTop: "顶部",
      railFullscreen: "全屏",
      railExitFullscreen: "退出",
      railFocus: "专注",
      railExitFocus: "退出",
      chapterMetaLine: (verses, count, minutes) =>
        `共 ${verses} 节 · 约 ${count.toLocaleString()} 字 · 预计 ${minutes} 分钟`,
      chapterEndDivider: "— 本章完 —",
      chapterStatsSummary: (highlightsCount, notesCount) =>
        `本章标记：高亮 ${highlightsCount} 处 · 笔记 ${notesCount} 条`,
      markChapterRead: "标记本章已读",
      chapterMarkedRead: "本章已读完",
      chapterSharedNotesTitle: "本章分享（已审核）",
      pullNextHint: (title, subtitle) =>
        `继续上拉进入下一章：${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextRelease: (title, subtitle) =>
        `松手进入下一章：${title}${subtitle ? ` · ${subtitle}` : ""}`,
      pullNextBibleEnd: "全本圣经已读完 🎉",
      copyText: "复制文字",
      copyWithRef: "复制含章节",
      shareWhatsApp: "WhatsApp 分享",
      zhLabel: "和合本",
      enLabel: "King James Version",
      listen: "朗读",
      listenSelection: "朗读选中",
      listenChapter: "朗读本章",
      listenFromHere: "从此处朗读",
      listenSections: "从小结开始",
      pause: "暂停",
      resume: "继续",
      stop: "停止",
      skipPrev: "上一节",
      skipNext: "下一节",
      rate: "语速",
      voice: "音色",
      voiceAuto: "自动",
      autoAdvance: "连读下一章",
      autoAdvanceOn: "读完自动下一章",
      autoAdvanceOff: "读完即停",
      sleepTimer: "定时停止",
      sleepTimerOff: "关闭定时",
      sleepTimerChapter: "读完本章即停",
      sleepTimerMin: (m) => `${m} 分钟`,
      zenMode: "专注阅读 (Z)",
      exitZenMode: "退出专注 (Esc)",
      fullscreen: "全屏阅读 (F)",
      exitFullscreen: "退出全屏 (Esc)",
      wakeLock: "屏幕常亮",
      wakeLockActive: "屏幕常亮中 (点击关闭)",
      wakeLockInactive: "屏幕常亮已关闭 (点击开启)",
      wakeLockDesc: "读经时保持屏幕常亮，防止设备休眠锁屏",
      wakeLockUnsupported: "当前设备或浏览器不支持屏幕常亮",
      currentChapterLabel: (zhBook, enBook, chapterNum, total) =>
        `当前是：${zhBook} 第 ${chapterNum} 章${total ? `（共 ${total} 章）` : ""}${enBook ? ` · ${enBook} ${chapterNum}` : ""}`,
      moreControls: "朗读设置",
      speechHint: "中英交替，或只读一种语言",
      speechUnsupported: "当前浏览器不支持朗读，请使用 Chrome 或 Safari",
      collapseSpeech: "收起到侧边悬浮",
      expandSpeech: "展开朗读",
      listeningVerse: (n, lang) =>
        String(lang || "").startsWith("en")
          ? `第 ${n} 节 · English`
          : `第 ${n} 节 · 中文`,
      modeParallel: "中英交替",
      modeZh: "只读中文",
      modeEn: "只读英文",
      scrollToTop: "回到顶部",
    },
  },
};

const loadCompareChapters = async () => {
  const [zhModule, enModule] = await Promise.all([
    import("../data/bibleData"),
    import("../data/bibleDataEn"),
  ]);
  const enById = new Map(
    enModule.bibleChapters.map((chapter) => [chapter.id, chapter])
  );
  return zhModule.bibleChapters.map((chapter) => {
    const english = enById.get(chapter.id);
    const zhTitle = chapter.title.replace("# ", "");
    const enTitle = english ? english.title.replace("# ", "") : "";
    return {
      ...chapter,
      title: enTitle ? `# ${zhTitle} · ${enTitle}` : chapter.title,
      altBookName: english ? bookNameFromChapter(english) : "",
      altContent: english ? english.content : "",
    };
  });
};

export const getBookName = bookNameFromChapter;

export const getEditionFromPath = (pathname = "") => {
  if (pathname === "/compare" || pathname.startsWith("/compare/")) {
    return EDITIONS.compare;
  }
  if (pathname === "/en" || pathname.startsWith("/en/")) {
    return EDITIONS.en;
  }
  return EDITIONS.zh;
};

export const switchEditionPath = (pathname, targetId) => {
  const target = EDITIONS[targetId] || EDITIONS.zh;
  const chapterMatch = pathname.match(/\/chapter\/([^/]+)/);
  if (chapterMatch) {
    return target.chapterPath(chapterMatch[1]);
  }
  if (pathname === "/about") {
    return "/about";
  }
  return target.homePath;
};

export const useEdition = () => {
  const { pathname } = useLocation();
  return getEditionFromPath(pathname);
};
